import { formatDate } from '@angular/common';
import { ElementRef, inject, Injectable } from '@angular/core';
import { FormBuilder, FormControlStatus, FormGroup, Validators } from '@angular/forms';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import moment from 'moment/moment';
import { filter, first, map, merge, Observable, of, switchMap, tap } from 'rxjs';

import {
  CreateRecourseCriteriaRestRequest,
  CreateRecourseCriteriaRestResponse,
  UpdateRecourseCriteriaRestRequest,
} from '../../../api/tur-wr/models';
import { RecourseCriteriaService } from '../../../api/tur-wr/services';
import { NotificationServiceWrapper } from '../../../services/notification-service-wrapper/notification-service-wrapper';
import { translate } from '../../../shared/fn/translate';
import { GlobalStore } from '../../../shared/global.store';
import { commentCharactersValidator } from '../validator/comment-characters-validator';

import { ObjectCriteriaGroup, WarrantyCalculationMethod, YesNo } from './model';
import { EnumType } from './recourse-criteria.component';

const formatToGermanDate = (dateAsString: string): string => formatDate(new Date(dateAsString), 'yyyy-MM-dd', 'de-DE');

const ERROR_KEY_OVERLAP = 'WR_RC_RECOURSE_CRITERIA_OVERLAP';

export const toTranslatedKeyValueObject = (element: KeyValueObject): KeyValueObject => ({
  ...element,
  value: translate(element.value),
});

export const isDateExpired = (validToDateAsString: string): boolean => {
  const todaysDate = new Date();
  const validToDate = new Date(validToDateAsString);
  return validToDate < todaysDate;
};

const determineIsInDisplayOnlyMode = (isInEditMode: boolean, validToDate: string): boolean =>
  isDateExpired(validToDate) && isInEditMode;

export const ASTERISK = '*';

export interface KeyValueObject {
  key: string;
  value: string;
}

interface KeyValueMap {
  [key: string]: string;
}

export const getKeyByValueIgnoreCase = <T>(enumeration: EnumType<T>, value: string): string | undefined =>
  Object.keys(enumeration).find(key => enumeration[key]?.toUpperCase() === value?.toUpperCase());

const WARRANTY_CALCULATION_METHOD_MAP: KeyValueMap = {
  [WarrantyCalculationMethod.Zp8]: 'warrantyCalculationMethod.ZP8',
  [WarrantyCalculationMethod.CustomerDelivery]: 'warrantyCalculationMethod.CUSTOMER_DELIVERY',
};

const RECOURSE_POSSIBLE_MAP: KeyValueMap = {
  [YesNo.Yes]: 'yesno.yes',
  [YesNo.No]: 'yesno.no',
};

const OBJECT_CRITERIA_GROUP_MAP: KeyValueMap = {
  [ObjectCriteriaGroup.Series]: 'objectCriteriaGroup.SERIES',
  [ObjectCriteriaGroup.SparePart]: 'objectCriteriaGroup.SPARE_PART',
  [ObjectCriteriaGroup.WildCard]: 'objectCriteriaGroup.WILD_CARD',
};

const toKeyValueObjectArray = (keyValueMap: KeyValueMap, values: string[]): KeyValueObject[] =>
  values.map((value: string) => ({
    key: value,
    value: keyValueMap[value],
  }));

export const WARRANTY_CALCULATION_METHOD_OPTIONS: KeyValueObject[] = toKeyValueObjectArray(
  WARRANTY_CALCULATION_METHOD_MAP,
  [WarrantyCalculationMethod.Zp8, WarrantyCalculationMethod.CustomerDelivery],
);

export const RECOURSE_POSSIBLE_OPTIONS: KeyValueObject[] = toKeyValueObjectArray(RECOURSE_POSSIBLE_MAP, [
  YesNo.Yes,
  YesNo.No,
]);

export const OBJECT_CRITERIA_GROUP_OPTIONS: KeyValueObject[] = toKeyValueObjectArray(OBJECT_CRITERIA_GROUP_MAP, [
  ObjectCriteriaGroup.WildCard,
  ObjectCriteriaGroup.Series,
  ObjectCriteriaGroup.SparePart,
]);

export type RecourseCriteria = CreateRecourseCriteriaRestResponse;

type NumberAsStringOrAsterisk = string | typeof ASTERISK;

interface RecourseCriteriaFormData {
  id?: number;
  comment: string;
  dealerNumber: string;
  isRecourseRelevant: KeyValueObject;
  mileage: NumberAsStringOrAsterisk;
  objectCriteriaGroup: KeyValueObject;
  sparePartNumber: string;
  supplierNumber: string;
  validFrom: string;
  validTo: string;
  warrantyCalculationMethod: KeyValueObject;
  warrantyDays: NumberAsStringOrAsterisk;
}

const defaultState = { recourseCriteria: [], formGroup: undefined, modalTemplate: undefined };

interface RecorseCriteriaState {
  recourseCriteria: RecourseCriteria[];
  formGroup: FormGroup;
  modalTemplate: ElementRef;
}

@Injectable()
export class RecourseCriteriaStore extends ComponentStore<RecorseCriteriaState> {
  private readonly recourseCriteriaService = inject(RecourseCriteriaService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly globalStore = inject(GlobalStore);
  private readonly notificationServiceWrapper = inject(NotificationServiceWrapper);

  public readonly recourseCriteria$: Observable<RecourseCriteria[]> = this.select(state => state.recourseCriteria);

  public readonly formGroup$ = this.select(state => state.formGroup);

  public readonly isModalOpenInDisplayOnlyMode$: Observable<boolean> = this.select(this.formGroup$, formGroup => {
    const isInEditMode = !!formGroup.get('id').value;
    const validToDate = formGroup.get('validTo').value;
    return determineIsInDisplayOnlyMode(isInEditMode, validToDate);
  });

  public readonly isFormValid$ = this.formGroup$.pipe(
    filter(Boolean),
    switchMap((form): Observable<FormControlStatus> =>
      merge(
        of(form.valid).pipe(map((isValid: boolean): FormControlStatus => (isValid ? 'VALID' : 'INVALID'))),
        form.statusChanges,
      ),
    ),
    map((status: FormControlStatus) => status === 'VALID'),
  );

  public readonly isFormValidAndModalNotOpenInDisplayOnlyMode$ = this.select(
    this.isFormValid$,
    this.isModalOpenInDisplayOnlyMode$,
    (isFormValid, isModalOpenInDisplayOnlyMode) => isFormValid && !isModalOpenInDisplayOnlyMode,
  );

  public constructor() {
    super(defaultState);
  }

  public ngrxOnStoreInit(): void {
    this.loadRecourseCriteria();
  }

  public readonly loadRecourseCriteria = this.effect(trigger$ =>
    trigger$.pipe(
      switchMap(() => this.recourseCriteriaService.findAllRecourseCriteria()),
      tap((recourseCriteria: RecourseCriteria[]) => this.patchState({ recourseCriteria })),
    ),
  );

  public readonly addRecourseCriteria = this.effect(trigger$ =>
    trigger$.pipe(
      tap(() => this.initForm()),
      switchMap(() => this.select(state => state.modalTemplate).pipe(first())),
      tap((elementRef: ElementRef) => this.globalStore.openModal(elementRef)),
    ),
  );

  public readonly editRecourseCriteria = this.effect((id$: Observable<number>) =>
    id$.pipe(
      switchMap(id => this.recourseCriteriaService.findRecourseCriteriaById({ id })),
      tap((recourseCriteria: RecourseCriteria) => this.initForm(recourseCriteria)),
      switchMap(() => this.select(state => state.modalTemplate).pipe(first())),
      tap((elementRef: ElementRef) => this.globalStore.openModal(elementRef)),
    ),
  );

  public readonly saveOrUpdateRecourseCriteria = this.effect(trigger$ => {
    const nonAsteriskValueOrNull = (value: string): string | null => (value === ASTERISK ? null : value);
    const nonAsteriskStringValueAsNumberOrNull = (value: string): number | null =>
      value === ASTERISK ? null : Number(value);
    const booleanStringToBoolean = (value: string): boolean => value === YesNo.Yes;

    const createSavePayload = (data: RecourseCriteriaFormData): CreateRecourseCriteriaRestRequest => ({
      comment: data.comment,
      dealerNumber: data.dealerNumber,
      isRecourseRelevant: booleanStringToBoolean(data.isRecourseRelevant.key),
      mileage: nonAsteriskStringValueAsNumberOrNull(data.mileage),
      objectCriteriaGroup: nonAsteriskValueOrNull(data.objectCriteriaGroup.key) as ObjectCriteriaGroup,
      sparePartNumber: data.sparePartNumber,
      supplierNumber: data.supplierNumber,
      validFrom: formatToGermanDate(data.validFrom),
      validTo: formatToGermanDate(data.validTo),
      warrantyCalculationMethod: data.warrantyCalculationMethod.key as WarrantyCalculationMethod,
      warrantyDays: nonAsteriskStringValueAsNumberOrNull(data.warrantyDays),
    });

    const createUpdatePayload = (data: RecourseCriteriaFormData): UpdateRecourseCriteriaRestRequest => ({
      comment: data.comment,
      validTo: formatToGermanDate(data.validTo),
    });

    return trigger$.pipe(
      tap(() => this.globalStore.setProcessing(true)),
      switchMap(() => this.formGroup$.pipe(first())),
      map(formGroup => formGroup.value),

      switchMap((data: RecourseCriteriaFormData) => {
        const isAboutInitialCreate = data.id === null;
        const serviceCall = isAboutInitialCreate
          ? this.recourseCriteriaService.createRecourseCriteria({ body: createSavePayload(data) })
          : this.recourseCriteriaService.updateRecourseCriteria({ id: data.id, body: createUpdatePayload(data) });
        return serviceCall.pipe(
          tapResponse({
            next: () => {
              const title = translate('recourceCriteria.saveSuccess.title');
              const content = translate('recourceCriteria.saveSuccess.content');
              this.notificationServiceWrapper.addSuccessNotification(title, content);
              this.globalStore.closeModal();
            },
            error: (error: any) => {
              const isOverlap = error?.error?.exceptionKey === ERROR_KEY_OVERLAP;
              const title = translate('recourceCriteria.saveError.title');
              const content = translate(
                isOverlap ? 'recourceCriteria.saveError.overlap' : 'recourceCriteria.saveError.content',
              );
              this.notificationServiceWrapper.addErrorNotification(title, content);
              if (!isOverlap) {
                this.globalStore.closeModal();
              }
            },
            finalize: () => {
              this.globalStore.setProcessing(false);
            },
          }),
          tap(() => this.loadRecourseCriteria()),
        );
      }),
    );
  });

  public readonly cancelRecourseCriteriaEditing = this.effect(trigger$ =>
    trigger$.pipe(
      tap(() => this.globalStore.closeModal()),
      tap(() => this.destroyForm),
    ),
  );

  private readonly initForm = this.effect((triggerOrData$: Observable<void | RecourseCriteria>) => {
    const toWarrantyCalculationMethod = (value: WarrantyCalculationMethod): KeyValueObject => ({
      key: value,
      value: WARRANTY_CALCULATION_METHOD_MAP[value],
    });

    /* eslint-disable */
    function getReverseMapping<T extends Record<string, string>>(enumObj: T) {
      const reverseMap: Record<string, keyof T> = {};
      Object.keys(enumObj).forEach(key => {
        const value = enumObj[key];
        reverseMap[value] = key as keyof T;
      });
      return reverseMap;
    }

    const warrantyEnumConverter = getReverseMapping(WarrantyCalculationMethod);
    const ocgEnumConverter = getReverseMapping(ObjectCriteriaGroup);

    const toRecoursePossible = (value: boolean): KeyValueObject => ({
      key: String(value),
      value: RECOURSE_POSSIBLE_MAP[String(value)],
    });

    const toObjectCriteriaGroup = (value: ObjectCriteriaGroup): KeyValueObject => ({
      key: value,
      value: OBJECT_CRITERIA_GROUP_MAP[value],
    });

    /* eslint-disable */
    function getObjectCriteriaGroup(triggerOrData: CreateRecourseCriteriaRestResponse): KeyValueObject {
      return triggerOrData?.objectCriteriaGroup
        ? toTranslatedKeyValueObject(
            toObjectCriteriaGroup(ObjectCriteriaGroup[ocgEnumConverter[triggerOrData.objectCriteriaGroup]]),
          )
        : toTranslatedKeyValueObject(OBJECT_CRITERIA_GROUP_OPTIONS[0]);
    }

    /* eslint-disable */
    function getWarrantyCalculationMethod(triggerOrData: CreateRecourseCriteriaRestResponse): KeyValueObject {
      return triggerOrData?.warrantyCalculationMethod
        ? toTranslatedKeyValueObject(
            toWarrantyCalculationMethod(
              WarrantyCalculationMethod[warrantyEnumConverter[triggerOrData.warrantyCalculationMethod]],
            ),
          )
        : toTranslatedKeyValueObject(WARRANTY_CALCULATION_METHOD_OPTIONS[0]);
    }

    const toFormData = (triggerOrData: RecourseCriteria | undefined): RecourseCriteriaFormData =>
      ({
        id: triggerOrData?.id,
        sparePartNumber: triggerOrData?.sparePartNumber || ASTERISK,
        supplierNumber: triggerOrData?.supplierNumber || ASTERISK,
        dealerNumber: triggerOrData?.dealerNumber || ASTERISK,
        warrantyDays: triggerOrData?.warrantyDays || ASTERISK,

        warrantyCalculationMethod: getWarrantyCalculationMethod(triggerOrData),

        mileage: triggerOrData?.mileage || ASTERISK,

        isRecourseRelevant:
          triggerOrData?.isRecourseRelevant === undefined
            ? toTranslatedKeyValueObject(RECOURSE_POSSIBLE_OPTIONS[0])
            : toTranslatedKeyValueObject(toRecoursePossible(triggerOrData.isRecourseRelevant)),

        objectCriteriaGroup: getObjectCriteriaGroup(triggerOrData),

        comment: triggerOrData?.comment || '',
        validFrom: triggerOrData?.validFrom || formatToGermanDate(moment().clone().add(1, 'days').toISOString()),
        validTo: triggerOrData?.validTo || formatToGermanDate(new Date(9999, 11, 31).toDateString()),
      }) as RecourseCriteriaFormData;

    const createFormGroup = (data: RecourseCriteriaFormData): FormGroup => {
      const isInEditMode = !!data.id;
      const isInDisplayOnlyMode = determineIsInDisplayOnlyMode(isInEditMode, data.validTo);

      return this.formBuilder.group({
        id: [data.id],
        sparePartNumber: this.formBuilder.control(
          { value: data.sparePartNumber, disabled: isInEditMode || isInDisplayOnlyMode },
          [Validators.required, Validators.pattern(/^(\*|\w{9,14})$/)],
        ),
        supplierNumber: this.formBuilder.control(
          { value: data.supplierNumber, disabled: isInEditMode || isInDisplayOnlyMode },
          [Validators.required, Validators.pattern(/^(\*|\d{6})$/)],
        ),
        dealerNumber: this.formBuilder.control(
          { value: data.dealerNumber, disabled: isInEditMode || isInDisplayOnlyMode },
          [Validators.required, Validators.pattern(/^(\*|\d{3,7})$/)],
        ),
        warrantyDays: this.formBuilder.control(
          { value: data.warrantyDays, disabled: isInEditMode || isInDisplayOnlyMode },
          [Validators.required, Validators.pattern(/^(\*|[1-9]\d{0,3})$/)],
        ),
        warrantyCalculationMethod: this.formBuilder.control(
          { value: data.warrantyCalculationMethod, disabled: isInEditMode || isInDisplayOnlyMode },
          [Validators.required],
        ),
        mileage: this.formBuilder.control({ value: data.mileage, disabled: isInEditMode || isInDisplayOnlyMode }, [
          Validators.required,
          Validators.pattern(/^(\*|[1-9]\d{0,5})$/),
        ]),
        isRecourseRelevant: this.formBuilder.control(
          { value: data.isRecourseRelevant, disabled: isInEditMode || isInDisplayOnlyMode },
          [Validators.required],
        ),
        objectCriteriaGroup: this.formBuilder.control(
          { value: data.objectCriteriaGroup, disabled: isInEditMode || isInDisplayOnlyMode },
          [Validators.required],
        ),
        comment: this.formBuilder.control({ value: data.comment, disabled: isInDisplayOnlyMode }, [
          Validators.pattern(/^.{0,280}$/),
          commentCharactersValidator,
        ]),
        validFrom: this.formBuilder.control({ value: data.validFrom, disabled: isInEditMode || isInDisplayOnlyMode }, [
          Validators.required,
        ]),
        validTo: this.formBuilder.control({ value: data.validTo, disabled: isInDisplayOnlyMode }, [
          Validators.required,
        ]),
      });
    };

    return triggerOrData$.pipe(
      map((triggerOrData: RecourseCriteria | undefined): RecourseCriteriaFormData => toFormData(triggerOrData)),
      tap((data: RecourseCriteriaFormData) => {
        this.patchState({ formGroup: createFormGroup(data) });
      }),
    );
  });

  private readonly destroyForm = this.effect(trigger$ =>
    trigger$.pipe(tap(() => this.patchState({ formGroup: undefined }))),
  );
}
